/**
 * V5 求解 API 服务层（集中化，修 06 文档坑「V4 fetch 散落各组件」）
 *
 * 规则（§1.1 冻结契约 + AGENTS.md）：
 *   - 全部通过共享 services/api 实例访问 /api/v5/scheduling/*，严禁硬编码 host
 *   - SSE 例外：useSolveStreamV5 通过带会话 headers 的 fetch-SSE 消费（流响应不走 axios）
 *   - 权限码沿用 V4，不新增
 */

import type { SolverConfig } from '../types/solverV5';
import type { SolveRunV5, SolveResultV5 } from '../types/solverV5';
import api from './api';

/** 共享 axios 实例已经带 /api baseURL，此处只保留业务路径。 */
const BASE = '/v5/scheduling';
/** fetch-SSE 不走 axios baseURL，需要包含完整的同源 API 前缀。 */
const SSE_BASE = `/api${BASE}`;

// ── 通用请求封装 ──────────────────────────────────────────────────────────────

async function apiRequest<T>(
  url: string,
  method: 'GET' | 'POST' = 'GET',
  data?: unknown,
): Promise<T> {
  try {
    const response = await api.request<T>({ url, method, data });
    return response.data;
  } catch (error) {
    const response = (error as {
      response?: {
        status?: number;
        data?: { error?: unknown; message?: unknown };
      };
    }).response;
    if (response) {
      const responseMessage = response.data?.error ?? response.data?.message;
      throw new Error(
        typeof responseMessage === 'string'
          ? responseMessage
          : `HTTP ${response.status ?? 'error'}`
      );
    }
    throw error;
  }
}

// ── Solve ──────────────────────────────────────────────────────────────────────

export interface SolveRequest {
  batch_ids: number[];
  solve_start_date: string;
  solve_end_date: string;
  config?: Partial<SolverConfig>;
}

export interface SolveResponse {
  success: boolean;
  run_id?: number;
  error?: string;
}

/**
 * 发起 V5 求解（SOLVER_RUN_EXECUTE）
 * POST /api/v5/scheduling/solve
 */
export async function solveV5(req: SolveRequest): Promise<SolveResponse> {
  return apiRequest<SolveResponse>(`${BASE}/solve`, 'POST', req);
}

// ── Precheck ──────────────────────────────────────────────────────────────────

export interface PrecheckRequest {
  batch_ids: number[];
  solve_start_date: string;
  solve_end_date: string;
  config?: Partial<SolverConfig>;
}

export interface PrecheckResponse {
  success: boolean;
  warnings?: Array<{ code: string; message: string; [key: string]: unknown }>;
  errors?: Array<{ code: string; message: string; [key: string]: unknown }>;
  [key: string]: unknown;
}

/**
 * 预检（SOLVER_RUN_READ）
 * POST /api/v5/scheduling/precheck
 */
export async function precheckV5(req: PrecheckRequest): Promise<PrecheckResponse> {
  return apiRequest<PrecheckResponse>(`${BASE}/precheck`, 'POST', req);
}

// ── Preview Proposal ──────────────────────────────────────────────────────────

export interface PreviewRequest {
  batch_ids: number[];
  solve_start_date: string;
  solve_end_date: string;
  config?: Partial<SolverConfig>;
}

export interface PreviewResponse {
  success: boolean;
  preview?: unknown;
  capability_gap?: { code: string; message?: string };
  [key: string]: unknown;
}

/**
 * 预览提案（SOLVER_RUN_READ）
 * POST /api/v5/scheduling/preview-proposal
 */
export async function previewProposalV5(req: PreviewRequest): Promise<PreviewResponse> {
  return apiRequest<PreviewResponse>(`${BASE}/preview-proposal`, 'POST', req);
}

// ── Run List ──────────────────────────────────────────────────────────────────

export interface GetRunsResponse {
  success: boolean;
  data?: SolveRunV5[];
  [key: string]: unknown;
}

/**
 * 获取 V5 历史运行列表（SOLVER_RUN_READ）
 * GET /api/v5/scheduling/runs  （WHERE run_code LIKE 'V5-%'）
 */
export async function getRunsV5(): Promise<GetRunsResponse> {
  return apiRequest<GetRunsResponse>(`${BASE}/runs`);
}

// ── Run Status ────────────────────────────────────────────────────────────────

export interface GetRunStatusResponse {
  success: boolean;
  status?: string;
  stage?: string;
  [key: string]: unknown;
}

/**
 * 查询单次运行状态（机器路径，SOLVER_RUN_READ）
 * GET /api/v5/scheduling/runs/:id/status
 */
export async function getRunStatusV5(runId: number): Promise<GetRunStatusResponse> {
  return apiRequest<GetRunStatusResponse>(`${BASE}/runs/${runId}/status`);
}

// ── Run Result ────────────────────────────────────────────────────────────────

export interface GetResultResponse {
  success: boolean;
  data?: SolveResultV5;
  infeasibility_analysis?: unknown;
  objective_breakdown?: unknown;
  viz?: unknown;
  [key: string]: unknown;
}

/**
 * 获取求解结果（SOLVER_RUN_READ）
 * GET /api/v5/scheduling/runs/:id/result
 */
export async function getRunResultV5(runId: number): Promise<GetResultResponse> {
  return apiRequest<GetResultResponse>(`${BASE}/runs/${runId}/result`);
}

// ── Stop ──────────────────────────────────────────────────────────────────────

export interface StopResponse {
  success: boolean;
  message?: string;
}

/**
 * 停止求解（SOLVER_RUN_ABORT）
 * POST /api/v5/scheduling/runs/:id/stop
 */
export async function stopRunV5(runId: number): Promise<StopResponse> {
  return apiRequest<StopResponse>(`${BASE}/runs/${runId}/stop`, 'POST');
}

// ── Apply ─────────────────────────────────────────────────────────────────────

export interface ApplyResponse {
  success: boolean;
  data?: {
    batch_assignments_inserted?: number;
    standalone_assignments_inserted?: number;
    shift_plans_inserted?: number;
    shift_plans_reused?: number;
    locked_assignments_skipped?: number;
    locked_shift_conflicts?: number;
    [key: string]: unknown;
  };
  error?: string;
}

/**
 * 应用排班结果（SOLVER_RESULT_APPLY）
 * POST /api/v5/scheduling/runs/:id/apply
 */
export async function applyRunResultV5(runId: number): Promise<ApplyResponse> {
  return apiRequest<ApplyResponse>(`${BASE}/runs/${runId}/apply`, 'POST');
}

// ── SSE URL helper（流请求在 hook 里使用）──────────────────────────────────

/**
 * 返回 SSE 进度端点相对 URL（前端 hook 建 EventSource 时使用）
 * GET /api/v5/scheduling/runs/:id/progress  （SSE）
 *
 * 注意：此函数不发请求，只为 fetch-SSE 调用方构造 URL。
 */
export function getRunProgressSseUrl(runId: number): string {
  return `${SSE_BASE}/runs/${runId}/progress`;
}
